import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { MessagesSquare, Send, Trash2, CheckCircle2, RotateCcw, Loader2, RefreshCw } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const ConvList = ({ rows, activeId, onPick }) => (
  <div className="divide-y divide-neutral-100 dark:divide-neutral-800 overflow-y-auto max-h-[70vh]" data-testid="help-conv-list">
    {rows.length === 0 && <div className="p-6 text-center text-[13px] text-neutral-500">No messages yet.</div>}
    {rows.map(c => (
      <button key={c.id} data-testid={`help-conv-${c.id}`} onClick={() => onPick(c.id)}
        className={`w-full text-left px-4 py-3 transition-colors ${activeId === c.id ? 'bg-emerald-50 dark:bg-emerald-950/30' : 'hover:bg-neutral-50 dark:hover:bg-neutral-900'}`}>
        <div className="flex items-center justify-between gap-2">
          <span className={`truncate text-[13.5px] ${c.unread_admin ? 'font-semibold text-neutral-900 dark:text-white' : 'text-neutral-700 dark:text-neutral-200'}`}>{c.name}</span>
          <span className="shrink-0 text-[10.5px] text-neutral-500">{fmt(c.last_message_at)}</span>
        </div>
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <span className="truncate text-[12px] text-neutral-500">{c.last_sender === 'admin' ? 'You: ' : ''}{c.last_preview}</span>
          {c.unread_admin > 0
            ? <span data-testid={`help-conv-unread-${c.id}`} className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[10.5px] grid place-items-center">{c.unread_admin}</span>
            : c.status === 'closed' && <span className="shrink-0 text-[10px] uppercase tracking-wider text-neutral-400">Resolved</span>}
        </div>
      </button>
    ))}
  </div>
);

const Thread = ({ conv, onSend, onStatus, onDelete }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [conv.messages.length]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    if (await onSend(text.trim())) setText('');
    setBusy(false);
  };
  return (
    <div className="flex flex-col h-full min-h-[60vh]" data-testid="help-thread">
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-neutral-200 dark:border-neutral-800">
        <div className="min-w-0">
          <div className="font-semibold text-[14.5px] truncate">{conv.name}</div>
          <a href={`mailto:${conv.email}`} className="text-[12px] text-neutral-500 hover:text-emerald-600 truncate block">{conv.email}{conv.customer_email ? ' · signed-in customer' : ' · guest'}</a>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {conv.status === 'open'
            ? <button data-testid="help-resolve-btn" onClick={() => onStatus('closed')} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 text-[12px]"><CheckCircle2 size={13} /> Resolve</button>
            : <button data-testid="help-reopen-btn" onClick={() => onStatus('open')} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 text-[12px]"><RotateCcw size={13} /> Reopen</button>}
          <button data-testid="help-delete-btn" onClick={onDelete} className="p-1.5 rounded-md text-neutral-500 hover:text-red-500"><Trash2 size={14} /></button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-2.5 max-h-[55vh]">
        {conv.messages.map(m => (
          <div key={m.id} className={`flex ${m.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-[13.5px] whitespace-pre-wrap break-words ${m.sender === 'admin' ? 'bg-emerald-600 text-white rounded-br-md' : 'bg-neutral-100 dark:bg-neutral-800 rounded-bl-md'}`}>
              {m.text}
              <div className={`mt-1 text-[10.5px] ${m.sender === 'admin' ? 'text-white/70' : 'text-neutral-500'}`}>{fmt(m.created_at)}</div>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="border-t border-neutral-200 dark:border-neutral-800 p-3 flex items-end gap-2">
        <textarea data-testid="help-reply-input" rows={2} value={text} maxLength={2000} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(e); }}
          placeholder="Write a reply… (Ctrl/⌘ + Enter to send). The customer is also notified by email."
          className="flex-1 bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-lg px-3 py-2 text-[13.5px] outline-none focus:border-neutral-400 resize-none" />
        <button data-testid="help-reply-send" disabled={busy || !text.trim()} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2.5 text-[13.5px] font-semibold disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Reply
        </button>
      </form>
    </div>
  );
};

const MessagesPanel = ({ onUnreadChange }) => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [rows, setRows] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [conv, setConv] = useState(null);
  const [err, setErr] = useState('');

  const loadList = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/help/conversations`, { headers });
      setRows(data); onUnreadChange?.(data.filter(c => c.unread_admin > 0).length);
    } catch { setErr('Failed to load messages'); }
  }, [headers, onUnreadChange]);

  const loadConv = useCallback(async (id) => {
    try { setConv((await axios.get(`${API}/admin/help/conversations/${id}`, { headers })).data); }
    catch { setErr('Failed to load conversation'); }
  }, [headers]);

  useEffect(() => { loadList(); const t = setInterval(loadList, 15000); return () => clearInterval(t); }, [loadList]);
  useEffect(() => {
    if (!activeId) return undefined;
    loadConv(activeId).then(loadList);
    const t = setInterval(() => loadConv(activeId), 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, loadConv]);

  const send = async (text) => {
    try {
      const { data } = await axios.post(`${API}/admin/help/conversations/${activeId}/messages`, { text }, { headers });
      setConv(prev => ({ ...prev, messages: [...prev.messages, data] })); loadList(); return true;
    } catch (e) { setErr(e?.response?.data?.detail || 'Send failed'); return false; }
  };
  const setStatus = async (status) => {
    try { await axios.patch(`${API}/admin/help/conversations/${activeId}`, { status }, { headers }); setConv(prev => ({ ...prev, status })); loadList(); }
    catch { setErr('Update failed'); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this conversation permanently?')) return;
    try { await axios.delete(`${API}/admin/help/conversations/${activeId}`, { headers }); setActiveId(null); setConv(null); loadList(); }
    catch { setErr('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="messages-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-[24px] text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-2"><MessagesSquare size={20} className="text-neutral-400" /> Messages</h2>
          <p className="text-[12.5px] text-neutral-500">Customer messages from the website "Need help?" box.</p>
        </div>
        <button onClick={loadList} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2 text-[13px]"><RefreshCw size={13} /> Refresh</button>
      </div>
      {err && <div className="text-red-600 text-[13px]" data-testid="messages-error">{err}</div>}
      <div className="grid md:grid-cols-[320px,1fr] rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] overflow-hidden">
        <div className="border-b md:border-b-0 md:border-r border-neutral-200 dark:border-neutral-800"><ConvList rows={rows} activeId={activeId} onPick={(id) => { setErr(''); setActiveId(id); }} /></div>
        <div>{conv && conv.id === activeId
          ? <Thread conv={conv} onSend={send} onStatus={setStatus} onDelete={remove} />
          : <div className="h-full min-h-[40vh] grid place-items-center text-[13px] text-neutral-500">Select a conversation</div>}</div>
      </div>
    </div>
  );
};

export default MessagesPanel;
